import React from 'react';
import { X, Bot, Workflow, Clock, Cpu, Brain, Code, FileText, AlertCircle, CheckCircle, Zap, Target } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card/Card';
import { Separator } from '../../../components/ui/separator';
import { cn } from '../../../utils/cn';

export interface MessageInfo {
  id: string;
  // Basic message info
  timestamp: string;
  channel: 'email' | 'sms' | 'phone';
  status: 'sent' | 'delivered' | 'read' | 'failed' | 'pending';
  
  // Source information
  source: 'human' | 'ai' | 'workflow' | 'template';
  
  // AI Information (if AI generated)
  aiInfo?: {
    model: string;
    prompt: string;
    systemPrompt: string;
    thoughtProcess: string;
    confidence: number;
    tokensUsed: number;
    processingTime: number; // in ms
    temperature: number;
    maxTokens: number;
    fullInput: string;
    fullOutput: string;
    reasoning: string[];
  };
  
  // Workflow Information (if workflow executed)
  workflowInfo?: {
    executionId: string;
    workflowName: string;
    workflowVersion: string;
    triggerType: 'manual' | 'automatic' | 'scheduled';
    executionTime: number; // in ms
    steps: Array<{
      name: string;
      status: 'success' | 'failed' | 'skipped';
      duration: number;
      output?: any;
      error?: string;
    }>;
    variables: Record<string, any>;
  };
  
  // Message metadata
  metadata?: {
    subject?: string;
    attachmentCount?: number;
    editedByHuman?: boolean;
    originalContent?: string;
    revisionHistory?: Array<{
      timestamp: string;
      content: string;
      editedBy: string;
    }>;
    deliveryAttempts?: number;
    lastAttemptError?: string;
  };
  
  // Performance metrics
  performance?: {
    loadTime: number;
    renderTime: number;
    networkLatency: number;
  };
}

interface MessageInfoModalProps {
  messageInfo: MessageInfo | null;
  isOpen: boolean;
  onClose: () => void;
}

export const MessageInfoModal: React.FC<MessageInfoModalProps> = ({ messageInfo, isOpen, onClose }) => {
  if (!isOpen || !messageInfo) return null;

  const getSourceIcon = () => {
    switch (messageInfo.source) {
      case 'ai':
        return <Bot className="h-4 w-4 text-purple-600" />;
      case 'workflow':
        return <Workflow className="h-4 w-4 text-blue-600" />;
      case 'template':
        return <FileText className="h-4 w-4 text-green-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getSourceBadge = () => {
    switch (messageInfo.source) {
      case 'ai':
        return <Badge className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">AI Generated</Badge>;
      case 'workflow':
        return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">Workflow Execution</Badge>;
      case 'template':
        return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Template Based</Badge>;
      case 'human':
        return <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">Human Written</Badge>;
      default:
        return null;
    }
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            {getSourceIcon()}
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Message Information
            </h2>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Source:</span>
                {getSourceBadge()}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Channel:</span>
                <span className="text-sm text-gray-900 dark:text-gray-100 capitalize">
                  {messageInfo.channel}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Status:</span>
                <Badge className={cn(
                  messageInfo.status === 'delivered' ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" :
                  messageInfo.status === 'read' ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200" :
                  messageInfo.status === 'failed' ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" :
                  "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
                )}>
                  {messageInfo.status}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Timestamp:</span>
                <span className="text-sm text-gray-900 dark:text-gray-100">
                  {new Date(messageInfo.timestamp).toLocaleString()}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* AI Information */}
          {messageInfo.aiInfo ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Brain className="h-4 w-4 text-purple-600" />
                  AI Processing Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Model:</span>
                    <div className="text-sm text-gray-900 dark:text-gray-100 font-mono">
                      {messageInfo.aiInfo.model}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Confidence:</span>
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {Math.round(messageInfo.aiInfo.confidence * 100)}%
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Processing Time:</span>
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {formatDuration(messageInfo.aiInfo.processingTime)}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Tokens Used:</span>
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {messageInfo.aiInfo.tokensUsed.toLocaleString()}
                    </div>
                  </div>
                </div>

                {/* AI Thought Process */}
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Thought Process:</span>
                  <div className="mt-1 p-3 bg-purple-50 dark:bg-purple-950/30 rounded-lg border border-purple-200 dark:border-purple-800">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {messageInfo.aiInfo.thoughtProcess}
                    </p>
                  </div>
                </div>

                {/* Reasoning Steps */}
                {messageInfo.aiInfo.reasoning && messageInfo.aiInfo.reasoning.length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Reasoning Steps:</span>
                    <div className="mt-1 space-y-2">
                      {messageInfo.aiInfo.reasoning.map((step, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <span className="flex-shrink-0 w-5 h-5 bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-full text-xs flex items-center justify-center">
                            {index + 1}
                          </span>
                          <span className="text-sm text-gray-700 dark:text-gray-300">{step}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Prompts */}
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">System Prompt:</span>
                    <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                      <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {messageInfo.aiInfo.systemPrompt}
                      </pre>
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">User Prompt:</span>
                    <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                      <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                        {messageInfo.aiInfo.prompt}
                      </pre>
                    </div>
                  </div>
                </div>

                {/* Model Parameters */}
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Model Parameters:</span>
                  <div className="mt-2 grid grid-cols-3 gap-4 text-xs">
                    <div>
                      <span className="text-gray-500">Temperature:</span>
                      <div className="font-mono">{messageInfo.aiInfo.temperature}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Max Tokens:</span>
                      <div className="font-mono">{messageInfo.aiInfo.maxTokens}</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Tokens Used:</span>
                      <div className="font-mono">{messageInfo.aiInfo.tokensUsed}</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : messageInfo.source === 'ai' ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Brain className="h-4 w-4 text-purple-600" />
                  AI Processing Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500 dark:text-gray-400">Coming soon</p>
              </CardContent>
            </Card>
          ) : null}

          {/* Workflow Information */}
          {messageInfo.workflowInfo ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Workflow className="h-4 w-4 text-blue-600" />
                  Workflow Execution Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Workflow:</span>
                    <div className="text-sm text-gray-900 dark:text-gray-100 font-medium">
                      {messageInfo.workflowInfo.workflowName}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Version:</span>
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {messageInfo.workflowInfo.workflowVersion}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Trigger:</span>
                    <div className="text-sm text-gray-900 dark:text-gray-100 capitalize">
                      {messageInfo.workflowInfo.triggerType}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Execution Time:</span>
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {formatDuration(messageInfo.workflowInfo.executionTime)}
                    </div>
                  </div>
                </div>

                {/* Workflow Steps */}
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Execution Steps:</span>
                  <div className="mt-2 space-y-2">
                    {messageInfo.workflowInfo.steps.map((step, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-900 rounded-lg">
                        <div className="flex items-center gap-3">
                          {step.status === 'success' ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : step.status === 'failed' ? (
                            <AlertCircle className="h-4 w-4 text-red-500" />
                          ) : (
                            <Clock className="h-4 w-4 text-gray-400" />
                          )}
                          <span className="text-sm text-gray-900 dark:text-gray-100">{step.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">{formatDuration(step.duration)}</span>
                          <Badge className={cn(
                            step.status === 'success' ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200" :
                            step.status === 'failed' ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200" :
                            "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200"
                          )}>
                            {step.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Workflow Variables */}
                {Object.keys(messageInfo.workflowInfo.variables).length > 0 && (
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Variables:</span>
                    <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                      <pre className="text-xs text-gray-700 dark:text-gray-300">
                        {JSON.stringify(messageInfo.workflowInfo.variables, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : messageInfo.source === 'workflow' ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Workflow className="h-4 w-4 text-blue-600" />
                  Workflow Execution Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500 dark:text-gray-400">Coming soon</p>
              </CardContent>
            </Card>
          ) : null}

          {/* Message Metadata */}
          {messageInfo.metadata && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Message Metadata
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {messageInfo.metadata.subject && (
                  <div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">Subject:</span>
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {messageInfo.metadata.subject}
                    </div>
                  </div>
                )}
                {messageInfo.metadata.attachmentCount !== undefined && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Attachments:</span>
                    <span className="text-sm text-gray-900 dark:text-gray-100">
                      {messageInfo.metadata.attachmentCount}
                    </span>
                  </div>
                )}
                {messageInfo.metadata.editedByHuman && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Human Edited:</span>
                    <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                      Yes
                    </Badge>
                  </div>
                )}
                {messageInfo.metadata.deliveryAttempts && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Delivery Attempts:</span>
                    <span className="text-sm text-gray-900 dark:text-gray-100">
                      {messageInfo.metadata.deliveryAttempts}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Performance Metrics */}
          {messageInfo.performance && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Zap className="h-4 w-4 text-orange-600" />
                  Performance Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {formatDuration(messageInfo.performance.loadTime)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Load Time
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {formatDuration(messageInfo.performance.renderTime)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Render Time
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {formatDuration(messageInfo.performance.networkLatency)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Network Latency
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button variant="outline" className="flex-1" onClick={onClose}>
              Close
            </Button>
            <Button variant="outline" size="icon">
              <Code className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}; 